import type { MilestoneDatesDto, SourcingEventV2PatchDto } from "../../../../../autogen/bff-api";
import type { MilestoneKey } from "./milestones";

export const daysBetween = (anchorIso: string, dateIso: string): number =>
  Math.round((new Date(dateIso).getTime() - new Date(anchorIso).getTime()) / 86_400_000);

/**
 * The tilbudsfrist the rest of the plan hangs off. Identical to the real deadline for single-stage;
 * for a two-stage competition before the § 23-2 invitation the server projects one from the
 * forespørselsfrist, so the downstream milestones still have something to anchor on.
 */
export const tenderAnchor = (dates: MilestoneDatesDto): string | null =>
  dates.plannedTenderDeadline ?? dates.bidSubmissionDeadline ?? null;

export const milestoneOffsetPatch = (
  nodeKey: MilestoneKey,
  pickedIso: string,
  dates: MilestoneDatesDto,
  // Two-stage competitions plan against a projected tilbudsfrist; the real one only exists after
  // the § 23-2 invitation, so the bid node moves the tender period instead of an absolute date.
  twoStage = false,
  requestDeadline: string | null = null,
): SourcingEventV2PatchDto | null => {
  switch (nodeKey) {
    case "questionDeadline": {
      const anchor = tenderAnchor(dates);
      if (!anchor) return null;
      return {
        eventData: {
          milestonePlan: {
            questionDaysBeforeBid: daysBetween(pickedIso, anchor),
          },
        },
      };
    }
    case "bidDeadline": {
      // Two-stage: the real tilbudsfrist is born at the § 23-2 invitation, so the buyer is moving
      // the planned tender period out from the forespørselsfrist instead.
      if (twoStage) {
        if (!requestDeadline) return null;
        return {
          eventData: {
            milestonePlan: {
              tenderPeriodOffsetDays: daysBetween(requestDeadline, pickedIso),
            },
          },
        };
      }
      return {
        eventData: {
          bidSubmissionDeadline: pickedIso,
        },
      };
    }
    case "evaluationStart": {
      const anchor = tenderAnchor(dates);
      if (!anchor) return null;
      return {
        eventData: {
          milestonePlan: {
            evaluationOffsetDays: daysBetween(anchor, pickedIso),
          },
        },
      };
    }
    case "awardDecision": {
      if (!dates.evaluationTarget) return null;
      return {
        eventData: {
          milestonePlan: {
            awardOffsetDays: daysBetween(dates.evaluationTarget, pickedIso),
          },
        },
      };
    }
    case "contractSigning": {
      const anchor = dates.karensDate ?? dates.awardTarget;
      if (!anchor) return null;
      return {
        eventData: {
          milestonePlan: {
            contractSigningOffsetDays: daysBetween(anchor, pickedIso),
          },
        },
      };
    }
    default:
      return null;
  }
};
